import { useState, useEffect, useCallback, useMemo } from "react";
import userStorage from "@/lib/user-storage";
import {
  generateTrainingPlan,
  type ITrainingPlanDay,
} from "@/data/training-plan-generator";
import {
  generateInjuryRecoveryPlan,
  getTodayRecoveryPlan,
  INJURY_ACTION_POOL,
  INJURY_PART_MAP,
  DAILY_RELAX_ACTIONS,
  type IRecoveryPlan,
  type InjuryPart,
} from "@/data/injury-recovery-plan";
import {
  useUserProfile,
  useSyncedState,
  useTrainingRecords,
  type IDailyHealthRecord,
} from "./useAppState";
import { useMemberStatus } from "./useMemberStatus";
import { healthTrainingAdjustment, displayCheckInMood } from "@/lib/healthTrainingAdjustment";
import { useDailyHealth } from "./useDailyHealth";
import { useTrainingDate } from "./useTrainingSchedule";
import {
  effectiveMemberDay,
  memberDayId,
  localTrainingDate,
} from "@/lib/memberTrainingDay";
import { useMemberPlanState } from "./useMemberPlanState";
import { trainingPlanAtDate } from '@/lib/trainingScheduleHistory';

const RECOVERY_PLAN_KEY = "injury_recovery_plan";
const RECOVERY_COMPLETED_KEY = "injury_recovery_completed_dates";
const AEROBIC_COMPLETED_KEY = "aerobic_completed_dates";

/** 取最近 N 天的平均睡眠小时数；一天都没有记录返回 null */
function getRecentSleepHours(
  records: IDailyHealthRecord[],
  days: number,
): number | null {
  const today = localTrainingDate();
  let total = 0;
  let count = 0;
  for (let i = 0; i < days; i++) {
    const d = new Date(today + "T00:00:00");
    d.setDate(d.getDate() - i);
    const dateStr = localTrainingDate(d);
    const rec = records.find((r) => r.date === dateStr);
    if (rec && typeof rec.sleepHours === "number") {
      total += rec.sleepHours;
      count++;
    }
  }
  return count > 0 ? +(total / count).toFixed(1) : null;
}

export function useMemberTrainingPlan() {
  const { member } = useMemberStatus();
  const { profile } = useUserProfile();
  const { records: healthRecords } = useDailyHealth();
  const [storedPlan, setMemberPlan] = useMemberPlanState();
  const currentDate = useTrainingDate();
  const memberPlan = useMemo(() => member.isMember ? trainingPlanAtDate(storedPlan, new Date()) : null, [member.isMember, storedPlan, currentDate]);
  const { addRecord } = useTrainingRecords();

  const generateMemberPlan = useCallback(
    (input: Parameters<typeof generateTrainingPlan>[0]) => {
      const plan = generateTrainingPlan(input);
      if (member.isMember) setMemberPlan(plan);
      return plan;
    },
    [member.isMember, setMemberPlan],
  );

  // ========== 伤病恢复计划 ==========
  const [recoveryPlan, setRecoveryPlan] = useState<IRecoveryPlan | null>(() => {
    try {
      if (!member.isMember) return null;
      const raw = userStorage.getItem(RECOVERY_PLAN_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [recoveryCompletedDates, setRecoveryCompletedDates] = useSyncedState<
    string[]
  >(RECOVERY_COMPLETED_KEY, []);

  useEffect(() => {
    if (recoveryPlan && member.isMember) {
      userStorage.setItem(RECOVERY_PLAN_KEY, JSON.stringify(recoveryPlan));
    } else if (!recoveryPlan) {
      userStorage.removeItem(RECOVERY_PLAN_KEY);
    }
  }, [recoveryPlan, member.isMember]);

  // 会员身份切换：非会员清空恢复计划
  useEffect(() => {
    if (!member.isMember && recoveryPlan !== null) {
      setRecoveryPlan(null);
      userStorage.removeItem(RECOVERY_PLAN_KEY);
    }
  }, [member.isMember, recoveryPlan]);

  const generateRecoveryPlan = useCallback(
    (
      injuryParts: string[],
      sleepQuality: "good" | "normal" | "poor" = "normal",
    ) => {
      // 问卷存的是中文「肩/颈/膝…」，动作池用英文 key，做一次映射
      const mappedParts = injuryParts
        .map(
          (p) =>
            INJURY_PART_MAP[p] ||
            (INJURY_ACTION_POOL[p as InjuryPart] ? (p as InjuryPart) : null),
        )
        .filter((p): p is InjuryPart => !!p);
      const plan = generateInjuryRecoveryPlan(mappedParts, sleepQuality);
      setRecoveryPlan(plan);
      return plan;
    },
    [],
  );

  const clearRecoveryPlan = useCallback(() => setRecoveryPlan(null), []);

  // 会员态下，根据 profile 伤病部位 + 睡眠质量生成恢复计划
  // 伤病部位变化时重新生成（身体信号页记录/移除不适会触发）
  useEffect(() => {
    if (!member.isMember) return;
    const parts = profile.injuryParts || [];
    const mappedParts = parts
      .map(
        (p) =>
          INJURY_PART_MAP[p] ||
          (INJURY_ACTION_POOL[p as InjuryPart] ? (p as InjuryPart) : null),
      )
      .filter((p): p is InjuryPart => !!p);

    // 无伤病 且 睡眠正常 → 清空恢复计划（显示身体状态良好）
    if (mappedParts.length === 0 && profile.sleepQuality !== "poor") {
      if (recoveryPlan) clearRecoveryPlan();
      return;
    }

    // 已有计划且伤病部位一致 → 不重复生成
    if (recoveryPlan) {
      const currentParts = [...recoveryPlan.injuryParts].sort().join(",");
      const newParts = [...mappedParts].sort().join(",");
      if (currentParts === newParts) return;
    }

    // 首次生成 或 伤病部位变化 → 生成新计划
    generateRecoveryPlan(parts, profile.sleepQuality);
  }, [
    member.isMember,
    recoveryPlan,
    profile.injuryParts,
    profile.sleepQuality,
    generateRecoveryPlan,
    clearRecoveryPlan,
  ]);

  const todayRecovery = recoveryPlan
    ? getTodayRecoveryPlan(recoveryPlan)
    : null;

  // 有伤病 或 睡眠差 → 有恢复计划；否则返回 null（首页显示「身体状态良好」）
  const hasRecoveryPlan =
    recoveryPlan &&
    (recoveryPlan.injuryParts.length > 0 || profile.sleepQuality === "poor");
  const todayInjuryRecovery = hasRecoveryPlan ? todayRecovery : null;

  // 恢复状态三态：有伤病推送 / 睡眠差助眠版 / 身体状态良好（可选日常放松）
  // 基于 profile 静态值 + 实时打卡数据综合判断
  const todayStr = currentDate;
  const recoveryStatus: "injury" | "sleep-poor" | "good" = useMemo(() => {
    const parts = profile.injuryParts || [];
    if (parts.length > 0) return "injury";
    // 睡眠差：profile 标记为 poor 或 最近 1-2 天实际睡眠 < 6 小时
    if (profile.sleepQuality === "poor") return "sleep-poor";
    const recentSleep = getRecentSleepHours(healthRecords, 2);
    if (recentSleep !== null && recentSleep < 6) return "sleep-poor";
    return "good";
  }, [profile.injuryParts, profile.sleepQuality, healthRecords]);

  // 恢复建议详情（基于实时健康数据的动态文案）
  const recoveryInfo = useMemo(() => {
    const recentSleep = getRecentSleepHours(healthRecords, 2);
    const todayRec = healthRecords.find((r) => r.date === todayStr);
    const todayMood = todayRec?.mood;

    // 睡眠建议分档
    let sleepTip: string;
    let sleepTipLevel: "good" | "warn" | "poor" | "none";
    if (recentSleep === null) {
      sleepTip = "今日尚未打卡睡眠，去打卡看看状态吧";
      sleepTipLevel = "none";
    } else if (recentSleep >= 7) {
      sleepTip = "睡眠状态良好，可正常训练";
      sleepTipLevel = "good";
    } else if (recentSleep >= 6) {
      sleepTip = "睡眠略有不足，注意休息";
      sleepTipLevel = "warn";
    } else {
      sleepTip = "昨晚睡眠不足，建议今晚补充睡眠，今日训练已自动降量";
      sleepTipLevel = "poor";
    }

    // 心情联动：今日不佳追加放松建议
    const moodRelaxTip =
      displayCheckInMood(todayMood) === "tired"
        ? "今日心情不佳，建议安排 10 分钟深呼吸或拉伸放松"
        : "";

    return { sleepTip, sleepTipLevel, moodRelaxTip };
  }, [healthRecords, todayStr]);

  // 日常放松计划（身体状态良好时的可选轻入口，6 个通用动作中取 3-4 个）
  const dailyRelaxPlan = useMemo(() => {
    const actions = DAILY_RELAX_ACTIONS.slice(0, 4);
    const totalSec = actions.reduce(
      (s, a) => s + a.durationSec * (a.bilateral ? 2 : 1),
      0,
    );
    const totalMin = Math.max(5, Math.ceil(totalSec / 60));
    return {
      injuryParts: [] as InjuryPart[],
      focusPart: "mixed" as const,
      title: "日常放松·全身舒缓",
      actions,
      totalMin,
    };
  }, []);

  // 今日恢复是否已完成
  const todayRecoveryCompleted = recoveryCompletedDates.includes(todayStr);

  // ========== 有氧日完成记录 ==========
  const [aerobicCompletedDates, setAerobicCompletedDates] = useSyncedState<
    string[]
  >(AEROBIC_COMPLETED_KEY, []);

  const aerobicDay = memberPlan ? effectiveMemberDay(memberPlan) : null;
  const aerobicKey =
    memberPlan && aerobicDay
      ? `${todayStr}:${memberDayId(memberPlan, aerobicDay)}`
      : todayStr;
  const todayAerobicCompleted =
    aerobicCompletedDates.includes(aerobicKey) ||
    (!memberPlan?.dailyOverrides?.[todayStr] &&
      aerobicCompletedDates.includes(todayStr));

  const markTodayAerobicComplete = useCallback(() => {
    if (!member.isMember || !memberPlan || !aerobicDay || todayAerobicCompleted)
      return;
    if (!["cardio", "hiit", "active-rest"].includes(aerobicDay.type)) return;
    setAerobicCompletedDates((prev) =>
      prev.includes(aerobicKey) ? prev : [...prev, aerobicKey],
    );
    const override = memberPlan.dailyOverrides?.[todayStr];
    addRecord({
      id: `rec-${Date.now()}`,
      date: todayStr,
      courseId: "member-aerobic",
      courseName: memberPlan.name,
      dayId: memberDayId(memberPlan, aerobicDay),
      dayName: aerobicDay.label,
      durationMin: aerobicDay.durationMin,
      totalSets: 0,
      completedSets: 0,
      exercises: [],
      memberPlanId: memberPlan.id,
      sourceKind: override?.kind || "scheduled",
      countsTowardPlan: true,
      replacedDayId: override
        ? memberDayId(memberPlan, override.originalDay)
        : undefined,
    });
  }, [
    member.isMember,
    memberPlan,
    aerobicDay,
    todayAerobicCompleted,
    aerobicKey,
    todayStr,
    addRecord,
    setAerobicCompletedDates,
  ]);

  const markTodayRecoveryComplete = useCallback(() => {
    setRecoveryCompletedDates((prev) => {
      if (prev.includes(todayStr)) return prev;
      return [...prev, todayStr];
    });
  }, [todayStr]);

  const clearMemberPlan = useCallback(() => {
    setMemberPlan(null);
  }, []);

  const getTodayWeekDay = useCallback((): ITrainingPlanDay | null => {
    return memberPlan ? effectiveMemberDay(memberPlan) : null;
  }, [memberPlan, currentDate]);

  // ========== 训练量智能联动（仅会员） ==========
  // 判定指标：
  //   1. 静息心率高于周均值 8 bpm → 异常
  //   2. 睡眠时长 < 6 小时 → 异常（5-6小时=轻度，<5小时=严重但统一记异常）
  //   3. 心情不佳（poor）→ 异常
  // 联动规则：
  //   0 项异常 → 正常训练
  //   1 项异常 → 降量 10%
  //   2 项异常 → 降量 20%
  //   3 项异常 → 建议休息，训练顺延
  const trainingAdjustment = useMemo(() => {
    return healthTrainingAdjustment(healthRecords, member.isMember);
  }, [member.isMember, healthRecords, currentDate]);

  return {
    memberPlan,
    setMemberPlan,
    generateMemberPlan,
    clearMemberPlan,
    getTodayWeekDay,
    recoveryPlan,
    generateRecoveryPlan,
    clearRecoveryPlan,
    todayInjuryRecovery,
    todayRecoveryCompleted,
    markTodayRecoveryComplete,
    todayAerobicCompleted,
    markTodayAerobicComplete,
    recoveryStatus,
    dailyRelaxPlan,
    trainingAdjustment,
    recoveryInfo,
  };
}
